import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Clock, CheckCircle, AlertTriangle, Calendar, Zap, Filter, Search } from 'lucide-react';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import ResponseDeploymentCard from './components/ResponseDeploymentCard';
import SchedulingPanel from './components/SchedulingPanel';
import PublishingProgressModal from './components/PublishingProgressModal';
import { automatedResponseService } from '../../services/automatedResponseService';
import { socialAccountService } from '../../services/socialAccountService';
import { brandService } from '../../services/brandService';

const MultiPlatformResponsePublisher = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approvedResponses, setApprovedResponses] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [selectedResponses, setSelectedResponses] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSchedulingPanel, setShowSchedulingPanel] = useState(false);
  const [showPublishingProgress, setShowPublishingProgress] = useState(false);
  const [publishingStatus, setPublishingStatus] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      loadConnectedAccounts(selectedBrand);
      loadApprovedResponses();
    }
  }, [selectedBrand, filterPlatform]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const brandsData = await brandService?.getAll();
      const activeBrands = brandsData?.filter(b => b?.isActive) || [];
      setBrands(activeBrands);
      
      if (activeBrands?.length > 0) {
        setSelectedBrand(activeBrands?.[0]?.id);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadConnectedAccounts = async (brandId) => {
    try {
      const accounts = await socialAccountService?.getByBrandId(brandId);
      setConnectedAccounts(accounts?.filter(acc => acc?.isConnected) || []);
    } catch (err) {
      console.error('Error loading connected accounts:', err);
    }
  };

  const loadApprovedResponses = async () => {
    try {
      setLoading(true);
      setError('');
      
      const responses = await automatedResponseService?.getApprovals({
        status: 'approved',
        platform: filterPlatform === 'all' ? null : filterPlatform
      });

      // Filter out already posted responses
      const pendingPublish = responses?.filter(r => !r?.autoPosted);

      // Apply search filter
      let filtered = pendingPublish;
      if (searchQuery?.trim()) {
        filtered = pendingPublish?.filter(r => 
          r?.finalResponse?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          r?.comment?.commentText?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          r?.comment?.commenterName?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        );
      }

      setApprovedResponses(filtered);
    } catch (err) {
      console.error('Error loading approved responses:', err);
      setError(err?.message || 'Failed to load approved responses');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectResponse = (responseId) => {
    setSelectedResponses(prev => 
      prev?.includes(responseId) 
        ? prev?.filter(id => id !== responseId)
        : [...prev, responseId]
    );
  };

  const handleSelectAll = () => {
    if (selectedResponses?.length === approvedResponses?.length) {
      setSelectedResponses([]);
    } else {
      setSelectedResponses(approvedResponses?.map(r => r?.id));
    }
  };

  const handlePublishNow = async (responseIds) => {
    try {
      setShowPublishingProgress(true);
      const responsesToPublish = responseIds || selectedResponses;
      
      const statusUpdates = responsesToPublish?.map(id => ({
        id,
        status: 'publishing',
        platform: approvedResponses?.find(r => r?.id === id)?.comment?.platform,
        progress: 0
      }));
      setPublishingStatus(statusUpdates);

      // Publish all responses simultaneously
      const results = await Promise.allSettled(
        responsesToPublish?.map(async (id) => {
          const response = approvedResponses?.find(r => r?.id === id);
          
          // Update progress
          setPublishingStatus(prev => 
            prev?.map(s => s?.id === id ? { ...s, progress: 50 } : s)
          );

          // Simulate publishing (in production, this would call actual social media APIs)
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Mark as posted in database
          await automatedResponseService?.approveResponse(
            id, 
            response?.finalResponse, 
            true
          );

          return { id, success: true };
        })
      );

      // Update final status
      const finalStatus = results?.map((result, index) => {
        const id = responsesToPublish?.[index];
        return {
          id,
          status: result?.status === 'fulfilled' ? 'success' : 'failed',
          platform: approvedResponses?.find(r => r?.id === id)?.comment?.platform,
          progress: 100,
          error: result?.status === 'rejected' ? result?.reason?.message : null
        };
      });
      setPublishingStatus(finalStatus);

      // Reload responses after 2 seconds
      setTimeout(() => {
        setShowPublishingProgress(false);
        setSelectedResponses([]);
        loadApprovedResponses();
      }, 2000);

    } catch (err) {
      console.error('Error publishing responses:', err);
      setError(err?.message || 'Failed to publish responses');
    }
  };

  const handleSchedulePublish = () => {
    if (selectedResponses?.length === 0) {
      setError('Please select at least one response to schedule');
      return;
    }
    setShowSchedulingPanel(true);
  };

  const handleScheduleConfirm = async (schedulingData) => {
    try {
      // In production, this would create scheduled tasks
      console.log('Scheduling responses:', schedulingData);
      
      setShowSchedulingPanel(false);
      setSelectedResponses([]);
      await loadApprovedResponses();
    } catch (err) {
      console.error('Error scheduling responses:', err);
      setError(err?.message || 'Failed to schedule responses');
    }
  };

  const getEngagementPrediction = (response) => {
    // Simple prediction based on response characteristics
    const hasEmoji = /[\u{1F600}-\u{1F64F}]/u?.test(response?.finalResponse);
    const hasQuestion = response?.finalResponse?.includes('?');
    const length = response?.finalResponse?.length;
    
    let score = 50;
    if (hasEmoji) score += 15;
    if (hasQuestion) score += 10;
    if (length > 50 && length < 200) score += 15;
    if (response?.safetyScore > 80) score += 10;
    
    return Math.min(score, 95);
  };

  const platformOptions = [
    { value: 'all', label: 'All Platforms' },
    { value: 'twitter', label: 'X (Twitter)' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'tiktok', label: 'TikTok' }
  ];

  if (loading && approvedResponses?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading approved responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Multi-Platform Response Publisher</h1>
              <p className="text-gray-600">
                Execute approved AI responses across all connected social platforms with intelligent scheduling
              </p>
            </div>
            <Button
              onClick={() => navigate('/ai-response-review-center')}
              variant="outline"
            >
              Back to Review Center
            </Button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Ready to Publish</p>
                  <p className="text-2xl font-bold text-gray-900">{approvedResponses?.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Selected</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedResponses?.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Connected Platforms</p>
                  <p className="text-2xl font-bold text-gray-900">{connectedAccounts?.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Avg. Engagement</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {approvedResponses?.length > 0 
                      ? Math.round(approvedResponses?.reduce((sum, r) => sum + getEngagementPrediction(r), 0) / approvedResponses?.length)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1">
              {/* Brand Selector */}
              <div className="w-full sm:w-48">
                <Select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e?.target?.value)}
                  options={brands?.map(b => ({ value: b?.id, label: b?.name }))}
                  placeholder="Select Brand"
                />
              </div>

              {/* Platform Filter */}
              <div className="w-full sm:w-48">
                <Select
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e?.target?.value)}
                  options={platformOptions}
                  placeholder="Filter by Platform"
                />
              </div>

              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search responses..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedResponses?.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handlePublishNow()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Publish Now ({selectedResponses?.length})
                </Button>
                <Button
                  onClick={handleSchedulePublish}
                  variant="outline"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule
                </Button>
              </div>
            )}
          </div>

          {/* Select All */}
          {approvedResponses?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedResponses?.length === approvedResponses?.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Select All ({approvedResponses?.length} responses)
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Response Deployment Cards */}
        {approvedResponses?.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Approved Responses</h3>
            <p className="text-gray-600 mb-4">
              You don't have any approved responses ready to publish.
            </p>
            <Button onClick={() => navigate('/ai-response-review-center')}>
              Go to Review Center
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {approvedResponses?.map(response => (
              <ResponseDeploymentCard
                key={response?.id}
                response={response}
                isSelected={selectedResponses?.includes(response?.id)}
                onSelect={() => handleSelectResponse(response?.id)}
                onPublishNow={() => handlePublishNow([response?.id])}
                connectedAccounts={connectedAccounts}
                engagementPrediction={getEngagementPrediction(response)}
              />
            ))}
          </div>
        )}

        {/* Scheduling Panel Modal */}
        {showSchedulingPanel && (
          <SchedulingPanel
            selectedResponses={approvedResponses?.filter(r => selectedResponses?.includes(r?.id))}
            connectedAccounts={connectedAccounts}
            onConfirm={handleScheduleConfirm}
            onClose={() => setShowSchedulingPanel(false)}
          />
        )}

        {/* Publishing Progress Modal */}
        {showPublishingProgress && (
          <PublishingProgressModal
            publishingStatus={publishingStatus}
            onClose={() => setShowPublishingProgress(false)}
          />
        )}
      </div>
    </div>
  );
};

export default MultiPlatformResponsePublisher;